/**
 * SmartCard - Premium Card Component
 * 
 * A sophisticated card component with:
 * - Spotlight hover effect
 * - Gradient border glow
 * - Adaptive shadow based on scroll depth
 * - Motion presence animations
 * - Responsive nesting
 * - Drag-enabled (optional)
 * - Glow trail effects
 * 
 * @component
 * @example
 * <SmartCard spotlight glow className="p-6">
 *   <h3>Lead Information</h3>
 * </SmartCard>
 */

import React, { useState, useEffect, useRef } from 'react';
import { motion, useMotionValue, useSpring, useScroll, useTransform } from 'framer-motion';
import { cn } from '@/lib/utils';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

export interface SmartCardProps extends React.HTMLAttributes<HTMLDivElement> {
  spotlight?: boolean;
  glow?: boolean;
  gradient?: boolean;
  drag?: boolean;
  dragConstraints?: { top?: number; left?: number; right?: number; bottom?: number };
  onDragEnd?: (event: any, info: any) => void;
  hover?: boolean;
  interactive?: boolean;
  elevation?: 'sm' | 'md' | 'lg' | 'xl';
  borderGlow?: boolean;
  children: React.ReactNode;
}

// ============================================================================
// SPOTLIGHT EFFECT COMPONENT
// ============================================================================

const SpotlightEffect: React.FC<{ active: boolean; x: number; y: number }> = ({ active, x, y }) => {
  return (
    <motion.div
      className="absolute inset-0 rounded-xl pointer-events-none overflow-hidden opacity-0"
      animate={{
        opacity: active ? 0.15 : 0,
      }}
      transition={{ duration: 0.3 }}
      style={{
        background: `radial-gradient(circle 300px at ${x}px ${y}px, rgba(255,255,255,0.1), transparent 70%)`,
      }}
    />
  );
};

// ============================================================================
// GRADIENT BORDER GLOW COMPONENT
// ============================================================================

const GradientBorderGlow: React.FC<{ active: boolean }> = ({ active }) => {
  return (
    <motion.div
      className="absolute inset-0 rounded-xl pointer-events-none opacity-0 border border-primary/30 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent"
      animate={{
        opacity: active ? 1 : 0,
      }}
      transition={{ duration: 0.3 }}
      style={{
        maskImage: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
        maskComposite: 'xor',
        WebkitMaskComposite: 'xor',
        padding: '1px',
      }}
    />
  );
};

// ============================================================================
// GLOW TRAIL COMPONENT
// ============================================================================

const GlowTrail: React.FC<{ active: boolean }> = ({ active }) => {
  const trails = Array.from({ length: 3 }, (_, i) => i);

  return (
    <>
      {trails.map((index) => (
        <motion.div
          key={index}
          className="absolute inset-0 rounded-xl pointer-events-none"
          animate={{
            opacity: active ? 0.3 - index * 0.1 : 0,
          }}
          style={{
            boxShadow: active
              ? `0 0 ${20 + index * 10}px hsl(var(--primary) / ${0.3 - index * 0.1})`
              : 'none',
          }}
          transition={{
            duration: 0.5,
            delay: index * 0.1,
          }}
        />
      ))}
    </>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const SmartCard = React.forwardRef<HTMLDivElement, SmartCardProps>(
  (
    {
      spotlight = false,
      glow = false,
      gradient = false,
      drag = false,
      dragConstraints,
      onDragEnd,
      hover = true,
      interactive = true,
      elevation = 'md',
      borderGlow = false,
      children,
      className,
      onMouseMove,
      ...props
    },
    ref
  ) => {
    const [isHovered, setIsHovered] = useState(false);
    const [spotlightPosition, setSpotlightPosition] = useState({ x: 0, y: 0 });
    const cardRef = useRef<HTMLDivElement>(null);
    const { scrollYProgress } = useScroll({
      target: cardRef,
      offset: ['start end', 'end start'],
    });

    // Adaptive shadow based on scroll depth
    const shadowIntensity = useTransform(scrollYProgress, [0, 1], [0.1, 0.3]);
    const shadowY = useTransform(scrollYProgress, [0, 1], [4, 12]);

    const elevationClasses = {
      sm: 'shadow-sm',
      md: 'shadow-md',
      lg: 'shadow-lg',
      xl: 'shadow-xl',
    };

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
      if (!cardRef.current || !spotlight) return;

      const rect = cardRef.current.getBoundingClientRect();
      setSpotlightPosition({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });

      onMouseMove?.(e);
    };

    const handleMouseEnter = () => {
      if (hover) {
        setIsHovered(true);
      }
    };

    const handleMouseLeave = () => {
      setIsHovered(false);
    };

    const useCardShell = typeof className === 'string' && className.includes('card-shell');
    return (
      <motion.div
        ref={(node) => {
          if (typeof ref === 'function') ref(node);
          else if (ref) ref.current = node;
          cardRef.current = node;
        }}
        className={cn(
          'relative rounded-xl transition-all duration-300',
          !useCardShell && 'border border-border/40 bg-card',
          useCardShell && 'rounded-2xl',
          elevationClasses[elevation],
          interactive && 'cursor-pointer',
          hover && isHovered && !useCardShell && 'border-primary/30 shadow-lg',
          hover && isHovered && useCardShell && 'shadow-lg',
          className
        )}
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        drag={drag ? 'y' : false}
        dragConstraints={dragConstraints}
        onDragEnd={onDragEnd}
        whileHover={hover ? { y: -2, scale: 1.01 } : {}}
        whileTap={interactive ? { scale: 0.99 } : {}}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          duration: 0.4,
          ease: [0.2, 0.8, 0.2, 1],
        }}
        style={{
          boxShadow: glow
            ? `0 ${shadowY.get()}px ${shadowY.get() * 2}px hsl(var(--primary) / ${shadowIntensity.get()})`
            : undefined,
        }}
        {...props}
      >
        {/* Gradient background overlay */}
        {gradient && (
          <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-primary/5 via-transparent to-primary/5 opacity-50" />
        )}

        {/* Spotlight effect */}
        {spotlight && (
          <SpotlightEffect
            active={isHovered}
            x={spotlightPosition.x}
            y={spotlightPosition.y}
          />
        )}

        {/* Gradient border glow */}
        {borderGlow && <GradientBorderGlow active={isHovered} />}

        {/* Glow trail */}
        {glow && <GlowTrail active={isHovered} />}

        {/* Content */}
        <div className="relative z-10">{children}</div>
      </motion.div>
    );
  }
);

SmartCard.displayName = 'SmartCard';

